import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { applyBrandColor } from "@/lib/theme";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MapPin, Save, Building2, Image as ImageIcon, KeyRound, CheckCircle2, PlugZap } from "lucide-react";
import CepField from "@/components/CepField";

const initial = {
  business_name: "", logo_url: "", cover_url: "", brand_color: "", pwa_icon_url: "", phone: "", whatsapp: "", email: "",
  instagram: "", website: "", tagline: "", origin_address: "", road_factor: 1.2,
  asaas_api_key: "", asaas_environment: "production", asaas_webhook_token: "",
};

export default function Settings() {
  const [form, setForm] = useState(initial);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/settings");
    setCurrent(data);
    setForm({
      ...initial,
      ...data,
      asaas_api_key: "",
      asaas_webhook_token: "",
      road_factor: data.road_factor || 1.2,
    });
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const testAsaas = async () => {
    setTesting(true); setTestResult(null);
    try {
      const body = {};
      if (form.asaas_api_key) body.api_key = form.asaas_api_key;
      if (form.asaas_environment) body.environment = form.asaas_environment;
      const { data } = await api.post("/settings/asaas/test", body);
      setTestResult(data);
      toast.success(`Conectado! ${data.account?.name || ""}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao testar conexão");
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...form, road_factor: Number(form.road_factor) });
      setCurrent(data);
      // Apply the new palette immediately (or reset when brand_color is empty)
      // so the UI doesn't stay stuck on a stale inline theme after a reset.
      applyBrandColor(data.brand_color || "");
      if (form.origin_address && data.origin_lat) toast.success("Configurações salvas — endereço localizado");
      else if (form.origin_address) toast.warning("Salvo, mas não conseguimos geolocalizar o endereço");
      else toast.success("Configurações salvas");
    } catch (e) { toast.error("Falha ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up max-w-3xl">
      <div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Configurações</h1>
        <p className="text-sm text-muted-foreground mt-1">Personalize a identidade do seu estabelecimento e o cálculo de deslocamento.</p>
      </div>

      {/* Identidade */}
      <div className="surface p-6 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          <Building2 className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold text-lg">Identidade da marca</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label>Nome do estabelecimento</Label>
            <Input data-testid="input-business-name" className="h-11" value={form.business_name} onChange={set("business_name")} placeholder="Ex.: Studio Beleza & Estilo" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Slogan / frase curta</Label>
            <Input data-testid="input-tagline" className="h-11" value={form.tagline} onChange={set("tagline")} placeholder="Ex.: Atendimento móvel em toda a Grande Vitória" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="flex items-center gap-2"><ImageIcon className="h-3.5 w-3.5" /> Logo (PNG/JPG, até 500KB)</Label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                data-testid="input-logo-file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 500 * 1024) { toast.error("Arquivo maior que 500KB"); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm((f) => ({ ...f, logo_url: reader.result }));
                  reader.readAsDataURL(file);
                }}
                className="text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer file:hover:opacity-90"
              />
              {form.logo_url && <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, logo_url: "" }))} data-testid="btn-remove-logo">Remover</Button>}
            </div>
            {form.logo_url && (
              <div className="mt-2 flex items-center gap-3 surface-muted p-3">
                <img alt="Preview" src={form.logo_url} className="h-16 w-16 object-contain rounded bg-white" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div className="text-xs text-muted-foreground">Prévia da logo</div>
              </div>
            )}
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="flex items-center gap-2"><ImageIcon className="h-3.5 w-3.5" /> Ícone do app (PWA) — PNG quadrado, até 500KB</Label>
            <p className="text-xs text-muted-foreground">Aparece quando o cliente adiciona o site à tela inicial do celular. Ideal: PNG quadrado 512×512, fundo sólido (o iOS não usa transparência).</p>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                data-testid="input-pwa-icon-file"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 500 * 1024) { toast.error("Arquivo maior que 500KB"); return; }
                  // Auto-crop to a 512×512 square canvas so the manifest icon is
                  // consistent regardless of the source aspect ratio.
                  const dataUrl = await new Promise((resolve, reject) => {
                    const r = new FileReader();
                    r.onload = () => resolve(r.result);
                    r.onerror = reject;
                    r.readAsDataURL(file);
                  });
                  const img = new Image();
                  img.onload = () => {
                    const size = 512;
                    const c = document.createElement("canvas");
                    c.width = size; c.height = size;
                    const ctx = c.getContext("2d");
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, size, size);
                    const scale = Math.min(size / img.width, size / img.height);
                    const w = img.width * scale, h = img.height * scale;
                    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
                    setForm((f) => ({ ...f, pwa_icon_url: c.toDataURL("image/png", 0.9) }));
                  };
                  img.src = dataUrl;
                }}
                className="text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer file:hover:opacity-90"
              />
              {form.pwa_icon_url && <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, pwa_icon_url: "" }))} data-testid="btn-remove-pwa-icon">Remover</Button>}
            </div>
            {form.pwa_icon_url && (
              <div className="mt-2 flex items-center gap-3 surface-muted p-3">
                <img alt="Prévia do ícone" src={form.pwa_icon_url} className="h-20 w-20 rounded-2xl object-cover shadow-lg" />
                <div className="text-xs text-muted-foreground">Prévia — assim que aparece na tela inicial do celular do cliente</div>
              </div>
            )}
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="flex items-center gap-2"><span className="inline-block h-3.5 w-3.5 rounded-full border" style={{ background: form.brand_color || "var(--primary)" }} /> Cor da marca</Label>
            <p className="text-xs text-muted-foreground">Essa cor colore os botões do painel, links, e o overlay das telas de login/agendamento.</p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                data-testid="input-brand-color"
                value={form.brand_color || "#2C4C3B"}
                onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))}
                className="h-11 w-16 rounded cursor-pointer border-0 p-0 bg-transparent"
                title="Escolher cor"
              />
              <Input
                data-testid="input-brand-color-hex"
                value={form.brand_color}
                onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))}
                placeholder="#2C4C3B"
                className="h-11 font-mono max-w-[160px]"
              />
              {form.brand_color && (
                <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, brand_color: "" }))} data-testid="btn-reset-color">Usar padrão</Button>
              )}
            </div>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="flex items-center gap-2"><ImageIcon className="h-3.5 w-3.5" /> Foto de capa da página pública (PNG/JPG, até 1MB)</Label>
            <p className="text-xs text-muted-foreground">Imagem que aparece no topo do <code>/agendar/{form.slug || "sua-franquia"}</code>. Recomendamos uma foto horizontal (proporção 3:1).</p>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                data-testid="input-cover-file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 1024 * 1024) { toast.error("Arquivo maior que 1MB"); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm((f) => ({ ...f, cover_url: reader.result }));
                  reader.readAsDataURL(file);
                }}
                className="text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer file:hover:opacity-90"
              />
              {form.cover_url && <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, cover_url: "" }))} data-testid="btn-remove-cover">Remover</Button>}
            </div>
            {form.cover_url && (
              <div className="mt-2 surface-muted p-3">
                <div className="relative rounded-lg overflow-hidden" style={{ aspectRatio: "3 / 1" }}>
                  <img alt="Prévia da capa" src={form.cover_url} className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">Prévia da capa (proporção 3:1)</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contato */}
      <div className="surface p-6 space-y-4">
        <div className="pb-2 border-b border-border">
          <h2 className="font-display font-semibold text-lg">Contato</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Telefone</Label><Input data-testid="input-phone" className="h-11" value={form.phone} onChange={set("phone")} placeholder="(27) 3333-4444" /></div>
          <div className="space-y-2"><Label>WhatsApp</Label><Input data-testid="input-whatsapp" className="h-11" value={form.whatsapp} onChange={set("whatsapp")} placeholder="(27) 99999-8888" /></div>
          <div className="space-y-2"><Label>Email</Label><Input data-testid="input-email-contact" type="email" className="h-11" value={form.email} onChange={set("email")} /></div>
          <div className="space-y-2"><Label>Instagram (@)</Label><Input data-testid="input-instagram" className="h-11" value={form.instagram} onChange={set("instagram")} placeholder="@seuestabelecimento" /></div>
          <div className="space-y-2 md:col-span-2"><Label>Website</Label><Input data-testid="input-website" className="h-11" value={form.website} onChange={set("website")} placeholder="https://seusite.com" /></div>
        </div>
      </div>

      {/* Deslocamento */}
      <div className="surface p-6 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          <MapPin className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold text-lg">Endereço de origem — deslocamento</h2>
        </div>
        <div className="space-y-2">
          <Label>Buscar por CEP (auto-preenche endereço + coordenadas)</Label>
          <CepField
            data-testid="cepfield-origin"
            onFound={(line) => {
              setForm((f) => ({ ...f, origin_address: line, origin_lat: null, origin_lon: null }));
              // Preview de coordenadas em tempo real: chama /geocode/distance
              // com o próprio endereço da base (retorna origin lat/lon após
              // geocodificar via BrasilAPI). Best-effort — se falhar, salvar
              // vai tentar de novo.
              (async () => {
                try {
                  const { data } = await api.post("/geocode/distance", { address: line });
                  if (data?.origin?.lat) {
                    setCurrent((c) => ({ ...(c || {}), origin_lat: data.origin.lat, origin_lon: data.origin.lon }));
                    toast.success(`Coord localizada: ${data.origin.lat.toFixed(5)}, ${data.origin.lon.toFixed(5)}`);
                  }
                } catch { /* silent — salvar faz nova tentativa */ }
              })();
            }}
          />
          <Label className="pt-2 block">Endereço fixo (origem para cálculo de km)</Label>
          <Textarea data-testid="input-origin-address" rows={2} value={form.origin_address} onChange={set("origin_address")} placeholder="Rua, número, bairro, cidade, UF, CEP" />
          <p className="text-xs text-muted-foreground">
            <strong>Importante:</strong> Digite o CEP acima e clique <em>Buscar</em> — o endereço completo e as coordenadas de partida são preenchidos automaticamente. Ao salvar, o sistema re-valida via BrasilAPI.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Fator de rota (linha reta → rota real; padrão 1.3)</Label>
          <Input data-testid="input-road-factor" type="number" step="0.05" min="1" className="h-11 w-32" value={form.road_factor} onChange={set("road_factor")} />
          <p className="text-xs text-muted-foreground">Multiplicador aplicado sobre a linha reta pra estimar a rota real. Padrão <strong>1.2</strong> (BR urbano). Se as ruas da sua região são muito sinuosas, tente 1.3.</p>
          <p className="text-xs text-muted-foreground">O cálculo já multiplica por 2 (ida + volta) automaticamente.</p>
        </div>
        {current?.origin_lat ? (
          <div className="surface-muted p-3 text-xs text-muted-foreground flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-primary" /> Localizado: {current.origin_lat.toFixed(5)}, {current.origin_lon.toFixed(5)}
          </div>
        ) : (
          <div className="surface-muted p-3 text-xs text-destructive">Endereço ainda não localizado. Salve para tentar geolocalizar.</div>
        )}
      </div>

      <div className="flex justify-end">
        <Button data-testid="btn-save-settings" onClick={save} disabled={saving} className="h-11">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salvar configurações
        </Button>
      </div>

      {/* Integrações */}
      <div className="surface p-6 space-y-4" data-testid="settings-asaas">
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          <PlugZap className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold text-lg">Integração Asaas (pagamentos)</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Cole aqui a chave da SUA conta Asaas — todas as cobranças que você gerar cairão direto na sua conta. Não sabe onde pegar?
          {" "}<a href="https://www.asaas.com/settings/api" target="_blank" rel="noreferrer" className="underline text-foreground">Ver instruções</a>.
        </p>

        {current?.asaas_configured ? (
          <div className="surface-muted p-3 flex items-center gap-2 text-sm" data-testid="asaas-status-configured">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <span>Chave ativa: <code className="font-mono text-xs">{current.asaas_api_key_masked}</code></span>
            <span className="ml-auto text-xs text-muted-foreground">Ambiente: {current.asaas_environment || "produção"}</span>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-400/50 bg-amber-50/70 dark:bg-amber-950/20 p-3 text-sm flex items-start gap-2" data-testid="asaas-status-missing">
            <span className="mt-0.5">⚠️</span>
            <div>
              <div className="font-medium">Nenhuma chave Asaas configurada.</div>
              <div className="text-xs text-muted-foreground mt-0.5">Enquanto isso, você continua agendando normalmente. Para gerar cobranças (PIX/boleto/cartão), cole sua chave e clique em <strong>Testar</strong>.</div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" /> Nova chave da API</Label>
            <Input
              data-testid="input-asaas-key"
              type="password"
              className="h-11 font-mono text-xs"
              value={form.asaas_api_key}
              onChange={set("asaas_api_key")}
              placeholder="$aact_prod_... ou $aact_hmlg_..."
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label>Ambiente</Label>
            <Select value={form.asaas_environment} onValueChange={set("asaas_environment")}>
              <SelectTrigger data-testid="select-asaas-env" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="production">Produção</SelectItem>
                <SelectItem value="sandbox">Sandbox (testes)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label>Token do webhook (opcional)</Label>
            <Input
              data-testid="input-asaas-webhook"
              type="password"
              className="h-11 font-mono text-xs"
              value={form.asaas_webhook_token}
              onChange={set("asaas_webhook_token")}
              placeholder="Deixe em branco para manter o atual"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">Configure no painel do Asaas em Integrações → Webhooks apontando para: <code className="text-xs">{window.location.origin}/api/webhooks/asaas</code></p>
          </div>

          {/* Emissão de NF-e (NFS-e) automática após a cobrança */}
          <div className="space-y-2 md:col-span-2">
            <Label>ID do Serviço Municipal no Asaas (opcional)</Label>
            <Input
              data-testid="input-asaas-municipal-service"
              className="h-11 font-mono text-xs"
              value={form.asaas_municipal_service_id || ""}
              onChange={set("asaas_municipal_service_id")}
              placeholder="Ex.: 12345 (encontrado em Asaas → NF-e → Configuração)"
            />
            <p className="text-xs text-muted-foreground">Necessário para o toggle "Emitir NF-e junto" agendar a nota automaticamente. Sem esse ID, você ainda pode emitir manualmente no painel do Asaas — nós já enviamos CEP/endereço do cliente.</p>
          </div>
          <div className="space-y-2">
            <Label>ISS % padrão (opcional)</Label>
            <Input
              data-testid="input-asaas-iss"
              type="number" min="0" max="10" step="0.01"
              className="h-11"
              value={form.asaas_default_iss_percent ?? ""}
              onChange={set("asaas_default_iss_percent")}
              placeholder="Ex.: 2.5"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button data-testid="btn-test-asaas" variant="outline" onClick={testAsaas} disabled={testing}>
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
            Testar conexão
          </Button>
          <Button data-testid="btn-save-asaas" onClick={save} disabled={saving} className="ml-auto">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Salvar chave e ambiente
          </Button>
          {testResult?.ok && (
            <div className="surface-muted px-3 py-2 text-xs flex items-center gap-2 basis-full">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Conectado — {testResult.account?.name} ({testResult.environment})
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
